import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { BackgroundJob, BackgroundJobStatusEnum } from 'graphql-common';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import Typography from '@lib/components/Typography/Typography';
import IconButton, {
  IconButtonSizes,
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import JobStatusChip from '@lib/components/JobStatusChip/JobStatusChip';
import { getAiErrorCodeText } from 'components/AiAssistant/utils/getAiErrorText';
import getJobTitle from 'components/JobTracker/utils/getJobTitle';
import getProgressDuration from 'components/JobTracker/utils/getProgressDuration';
import getJobIcon from 'components/JobTracker/utils/getJobIcon';
import getJobBadgeStatus from 'components/JobTracker/utils/getJobBadgeStatus';
import useActions from 'components/JobTracker/utils/useActions';
import styles from './JobTracker.module.scss';

const COUNTDOWN_TICK = 1000;

const isRunning = (status: BackgroundJobStatusEnum) =>
  status === BackgroundJobStatusEnum.Queued ||
  status === BackgroundJobStatusEnum.Working ||
  status === BackgroundJobStatusEnum.Retrying;

export default function JobPreview({
  job,
  removeJobId,
  isChild = false,
}: {
  job: BackgroundJob;
  removeJobId: (id: string) => void;
  isChild?: boolean;
}) {
  const { t } = useTranslation();
  // Re-render every second so the ETA derived from `estimatedFinishAt` counts
  // down smoothly between throttled progress frames.
  const [, setTick] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true);
  const running = isRunning(job.status);
  useEffect(() => {
    if (!running || !job.estimatedFinishAt) return undefined;
    const interval = setInterval(() => setTick((n) => n + 1), COUNTDOWN_TICK);
    return () => clearInterval(interval);
  }, [running, job.estimatedFinishAt]);

  const title = getJobTitle(job.kind, t);
  // A terminal job has no remaining time; a stale `estimatedFinishAt` would
  // otherwise render a meaningless (and reload-dependent) ETA.
  const progressDuration = running
    ? getProgressDuration(job.estimatedFinishAt, t)
    : '';
  const hasChildren = !!job.children?.length;
  const childrenVisible = hasChildren && isExpanded;
  const badgeStatus = getJobBadgeStatus(job, { isChild, childrenVisible });
  // A finished job carries no `message` — the BE clears it as the job
  // terminalizes, so the line under the badge would otherwise be empty. For a
  // failure `errorCode` says why in the same vocabulary the AI chat uses
  // ("No credits remaining"); an unclassified crash has neither, and the
  // status chip alone states the outcome.
  const failureReason =
    job.displayStatus === BackgroundJobStatusEnum.Failed
      ? getAiErrorCodeText(t, job.errorCode)
      : null;
  const {
    onRemoveHandler,
    canCancelJob,
    canDismissJob,
    actionLoading,
    canSendByEmail,
    deliveryRequested,
    onSendByEmailHandler,
    sendByEmailLoading,
    canRetry,
    onRetryHandler,
    retryLoading,
  } = useActions({ job, removeJobId });

  return (
    <div
      className={classNames(styles.jobGroup, {
        [styles.rootGroup]: !isChild,
      })}
    >
      <div
        className={classNames(styles.job, {
          [styles.jobChild]: isChild,
          [styles.jobWithExpander]: hasChildren,
        })}
      >
        {hasChildren && (
          <IconButton
            data-test-id="job-expander"
            icon={isExpanded ? 'expand_more' : 'chevron_right'}
            type={IconButtonTypes.QuaternaryFilled}
            size={IconButtonSizes.ExtraSmall}
            className={styles.expander}
            onClick={() => setIsExpanded((prevState) => !prevState)}
          />
        )}
        {getJobIcon(badgeStatus)}
        <div className={styles.jobContent}>
          <div className={styles.jobTitleWrap}>
            <div className={styles.jobTitleRow}>
              <Typography
                variant={isChild ? 'caption' : 'body'}
                strong
                className={styles.jobTitle}
              >
                {title}
              </Typography>
              <JobStatusChip
                data={{ status: badgeStatus, failed: job.failed }}
              />
            </div>
          </div>
          <div className={styles.jobMessageWrap}>
            <Typography variant="caption" className={styles.jobMessage}>
              {!!progressDuration && (
                <span>
                  {progressDuration}{' '}
                  <span className={styles.durationSeparator}>|</span>{' '}
                </span>
              )}
              <span
                className={classNames({
                  [styles.jobErrorMessage]: !!failureReason,
                })}
              >
                {failureReason ?? job.message}
              </span>
            </Typography>
          </div>
        </div>
        <div className={styles.jobActions}>
          {canSendByEmail && (
            <Tooltip body={t('send-by-email')}>
              <IconButton
                icon={deliveryRequested ? 'check' : 'mail'}
                symbolsOutlined
                type={IconButtonTypes.PrimaryOutlined}
                size={IconButtonSizes.Small}
                onClick={onSendByEmailHandler}
                disabled={deliveryRequested}
                isLoading={sendByEmailLoading}
              />
            </Tooltip>
          )}
          {canRetry && (
            <Tooltip body={t('try-again')}>
              <IconButton
                icon="refresh"
                symbolsOutlined
                type={IconButtonTypes.PrimaryOutlined}
                size={IconButtonSizes.Small}
                onClick={onRetryHandler}
                isLoading={retryLoading}
              />
            </Tooltip>
          )}
        </div>
        <div>
          {(canCancelJob || canDismissJob) && (
            <Tooltip body={canCancelJob ? t('cancel') : t('discard')}>
              <IconButton
                icon="cancel"
                type={IconButtonTypes.QuaternaryFilled}
                size={IconButtonSizes.Small}
                onClick={onRemoveHandler}
                rounded
                isLoading={actionLoading}
              />
            </Tooltip>
          )}
        </div>
      </div>
      {hasChildren && isExpanded && (
        <div className={styles.children}>
          {(job.children || []).map((child) => (
            <JobPreview
              key={child.id}
              job={child}
              removeJobId={removeJobId}
              isChild
            />
          ))}
        </div>
      )}
    </div>
  );
}
